import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import { AppPillSwitch } from '../../components/ui';
import { notify } from '../../components/common/AppToast';
import CronConfirmModal from '../../components/kkp/KkpCronConfirmModal';
import MediaPlanningFilters from '../../components/kkp/KkpCronMediaPlanningFilters';
import { AppAlert, AppButton, AppModal } from '../../components/ui';
import KkpCronsList from '../../components/kkp/KkpCronsList';
import KkpCronsWizard from '../../components/kkp/KkpCronsWizard';
import kkpService from '../../services/kkpService';
import authService from '../../services/authService';
import { ROLE_LABELS } from '../../config/userRoles';
import '../../styles/kkp-planning-view-mode.scss';
import '../../styles/kkp-crons-page.scss';
import {
  CANAUX_OPTIONS,
  DEFAULT_COMMERCIAUX_LUNDI_CANAUX,
  DEFAULT_COMMERCIAUX_LUNDI_WINDOW_DAYS,
  DEFAULT_RELANCE_MONTH_DAYS,
  buildAutoName,
  defaultCronName,
  emptyFilters,
  cronToForm,
  emptyForm,
  findDuplicateCrons,
  formatRecipientNames,
  formatRecipientsPhrase,
  hasCronTestMode,
  normalizeWeekAnchor,
  normalizeWeekInterval,
  weekAnchorMatchesWeekdays,
  isInstantCronType,
  isInstantRecipientsCronType,
  isSystemCronType,
  nextDuplicateCronName,
  normalizeCommerciauxLundiWindowDays,
} from '../../utils/kkpCronUi';

const CronTypePermissionsPanel = ({ roles, cronTypes, matrix, onChange, onSave, saving }) => (
  <div className="border rounded-3 p-3 bg-neutral-50">
    <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
      <div>
        <h6 className="mb-1 fw-semibold">
          <Icon icon="mdi:shield-account-outline" className="me-1" width={18} />
          Qui a le droit de créer chaque mail
        </h6>
        <p className="text-sm text-secondary-light mb-0">
          Cochez les rôles autorisés. Les Super Administrateurs voient toujours tout.
        </p>
      </div>
      <button type="button" className="btn btn-sm btn-success-600" disabled={saving} onClick={onSave}>
        Enregistrer les droits
      </button>
    </div>
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr>
            <th>Rôle</th>
            {cronTypes.map((type) => (
              <th key={type.id} className="text-center text-sm" style={{ minWidth: 120 }}>
                {type.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {roles.map((role) => (
            <tr key={role}>
              <td className="fw-semibold text-sm">{ROLE_LABELS[role] || role}</td>
              {cronTypes.map((type) => {
                const checked = Boolean(matrix?.[role]?.[type.id]);
                const inputId = `cron-perm-${role}-${type.id}`;
                return (
                  <td key={`${role}-${type.id}`} className="text-center">
                    <div className="form-check d-inline-flex justify-content-center m-0">
                      <input
                        id={inputId}
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={checked}
                        onChange={(e) => onChange(role, type.id, e.target.checked)}
                        style={{ width: '1.15rem', height: '1.15rem', cursor: 'pointer' }}
                      />
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const SendModal = ({ show, onClose, cron, meta, commerciaux, onSend }) => {
  const [filters, setFilters] = useState(emptyFilters);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (show && cron) {
      const merged = { ...emptyFilters(), ...(cron.filters || {}) };
      if (!(merged.statut_phase || []).length) {
        merged.statut_phase = ['Option'];
      }
      setFilters(merged);
    }
  }, [show, cron]);

  if (!show || !cron) return null;

  const handleSend = async () => {
    const statutPhase = [...(filters?.statut_phase || [])]
      .map((s) => String(s || '').trim())
      .filter(Boolean);
    if (statutPhase.length === 0) {
      notify.error('Choisissez au moins un statut.');
      return;
    }
    setSending(true);
    try {
      await onSend(cron.id, { ...filters, statut_phase: statutPhase });
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <AppModal
      show
      size="lg"
      centered
      scrollable
      onClose={onClose}
      headerClassName="d-flex justify-content-center position-relative"
      title={(
        <span className="text-center">
          <Icon icon="mdi:email-fast-outline" className="me-2" width={20} />
          Envoyer maintenant — {cron.name}
        </span>
      )}
      footer={(
        <>
          <AppButton variant="secondary" onClick={onClose}>Annuler</AppButton>
          <AppButton icon="mdi:send" loading={sending} loadingLabel="Envoi…" onClick={handleSend}>
            Envoyer maintenant
          </AppButton>
        </>
      )}
    >
            <p className="text-secondary-light mb-2">
              Vous pouvez changer les dossiers pour cet envoi seulement. L’alerte enregistrée ne change pas.
            </p>
            <MediaPlanningFilters
              filters={filters}
              onChange={setFilters}
              meta={meta}
              commerciaux={commerciaux}
            />
    </AppModal>
  );
};

const KkpCronsPage = () => {
  const currentUser = authService.getCurrentUser();
  const currentUserId = Number(currentUser?.id) || 0;
  const currentUserEmail = String(currentUser?.email || '').trim();

  const [crons, setCrons] = useState([]);
  const [users, setUsers] = useState([]);
  const [cronTypes, setCronTypes] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => emptyForm('cdf_dispo_mag', [], { testEmail: currentUserEmail }));
  const [editingId, setEditingId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [commerciaux, setCommerciaux] = useState([]);
  const [sendModal, setSendModal] = useState({ show: false, cron: null });
  const [confirmModal, setConfirmModal] = useState(null);
  const [permRoles, setPermRoles] = useState([]);
  const [permTypes, setPermTypes] = useState([]);
  const [permMatrix, setPermMatrix] = useState({});
  const [permSaving, setPermSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('crons');
  const [wizardStep, setWizardStep] = useState(0);
  const [nameTouched, setNameTouched] = useState(false);
  const [search, setSearch] = useState('');
  const [ownerScope, setOwnerScope] = useState('all');
  const [loadError, setLoadError] = useState(null);
  const editingIdRef = useRef(editingId);
  editingIdRef.current = editingId;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      let data;
      try {
        data = await kkpService.listCdfDispoAlertCrons();
      } catch {
        await new Promise((resolve) => setTimeout(resolve, 900));
        data = await kkpService.listCdfDispoAlertCrons();
      }
      setCrons(data.crons ?? []);
      setUsers(data.users ?? []);
      const types = data.cron_types ?? [];
      const mailTypes = types.filter((type) => !type.system && !type.instant && !isInstantCronType(type.id));
      setCronTypes(types);
      setIsSuperAdmin(Boolean(data.is_superadmin));
      setForm((prev) => {
        if (editingIdRef.current) return prev;
        const defaultType = mailTypes[0]?.id ?? 'cdf_dispo_mag';
        return prev.cron_type && !isSystemCronType(prev.cron_type) ? prev : emptyForm(defaultType, types, { testEmail: currentUserEmail });
      });
    } catch (e) {
      const message = e?.message || 'Impossible de charger les alertes mails.';
      setLoadError(message);
      notify.error(message);
    } finally {
      setLoading(false);
    }
  }, [currentUserEmail]);

  const loadPermissions = useCallback(async () => {
    try {
      const data = await kkpService.getCronTypePermissions();
      setPermRoles(data.roles ?? []);
      setPermTypes(data.cron_types ?? []);
      setPermMatrix(data.matrix ?? {});
    } catch (e) {
      notify.error(e?.message || 'Impossible de charger les droits.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (isSuperAdmin) {
      loadPermissions();
    }
  }, [isSuperAdmin, loadPermissions]);

  useEffect(() => {
    (async () => {
      try {
        const data = await kkpService.getMagMeta();
        setMeta(data);
        setCommerciaux(data.commerciaux ?? []);
      } catch {
        // meta optionnelle
      }
    })();
  }, []);

  const isDayOfMonthCron = form.cron_type === 'mails_clients_mag_set' || isSystemCronType(form.cron_type);
  const isRelanceVenteN1 = form.cron_type === 'relance_vente_n1';
  const mailCronTypes = useMemo(
    () => (cronTypes || []).filter((type) => (
      !type.system && !type.instant && !isSystemCronType(type.id) && !isInstantCronType(type.id)
    )),
    [cronTypes],
  );
  const duplicateCrons = useMemo(
    () => findDuplicateCrons(crons, form.cron_type, editingId),
    [crons, editingId, form.cron_type],
  );

  const beginCreate = (cronType) => {
    const nextType = cronType || mailCronTypes[0]?.id || 'cdf_dispo_mag';
    setForm(emptyForm(nextType, cronTypes, { testEmail: currentUserEmail }));
    setEditingId(null);
    setNameTouched(false);
    setWizardStep(cronType ? 1 : 0);
    setActiveTab('creer');
  };

  const resetForm = () => {
    setForm(emptyForm(mailCronTypes[0]?.id ?? 'cdf_dispo_mag', cronTypes, { testEmail: currentUserEmail }));
    setEditingId(null);
    setNameTouched(false);
    setWizardStep(0);
  };

  const startEdit = (cron) => {
    const cronType = cron.cron_type ?? 'cdf_dispo_mag';
    if (isInstantCronType(cronType) && !isInstantRecipientsCronType(cronType)) {
      return;
    }
    setEditingId(cron.id);
    setNameTouched(true);
    setWizardStep(isSystemCronType(cronType) ? 1 : 0);
    setForm(cronToForm(cron, cronTypes));
    setActiveTab('creer');
  };

  const startDuplicate = (cron) => {
    if (isInstantCronType(cron.cron_type)) {
      return;
    }
    const next = cronToForm(cron, cronTypes);
    const sourceName = (next.name || '').trim() || defaultCronName(next.cron_type, cronTypes);
    setEditingId(null);
    setNameTouched(true);
    setWizardStep(1);
    setForm({
      ...next,
      name: nextDuplicateCronName(sourceName, crons),
    });
    setActiveTab('creer');
    notify.success(`L’alerte « ${sourceName} » a bien été copiée.`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    if (isInstantRecipientsCronType(form.cron_type)) {
      try {
        await kkpService.updateCdfDispoAlertCron(editingId, {
          recipient_user_ids: form.recipient_user_ids,
          extra_emails: form.extra_emails,
        });
        notify.success('Destinataires enregistrés');
        resetForm();
        await load();
        setActiveTab('crons');
      } catch (err) {
        notify.error(err?.message || 'Enregistrement impossible.');
      } finally {
        setSaving(false);
      }
      return;
    }
    const weekdays = [...(form.weekdays || [])]
      .map(Number)
      .filter((d) => d >= 1 && d <= 7)
      .sort((a, b) => a - b);
    if (weekdays.length === 0 && !isDayOfMonthCron && !isRelanceVenteN1) {
      notify.error('Choisissez au moins un jour.');
      setWizardStep(1);
      setSaving(false);
      return;
    }
    const weekInterval = normalizeWeekInterval(form.filters?.week_interval);
    const weekAnchor = normalizeWeekAnchor(form.filters?.week_anchor);
    if (!isDayOfMonthCron && !isRelanceVenteN1 && weekInterval === 2) {
      if (!weekAnchor) {
        notify.error('Choisissez la date du premier envoi.');
        setWizardStep(1);
        setSaving(false);
        return;
      }
      if (!weekAnchorMatchesWeekdays(weekAnchor, weekdays)) {
        notify.error('La date du premier envoi doit tomber sur un jour coché.');
        setWizardStep(1);
        setSaving(false);
        return;
      }
    }
    if (
      form.cron_type !== 'commerciaux_lundi'
      && form.cron_type !== 'klub_etapes_lundi'
      && !isSystemCronType(form.cron_type)
    ) {
      const hasUsers = (form.recipient_user_ids || []).length > 0;
      const hasEmails = String(form.extra_emails || '').trim() !== '';
      if (!hasUsers && !hasEmails) {
        notify.error('Choisissez au moins une personne ou une autre adresse.');
        setWizardStep(2);
        setSaving(false);
        return;
      }
    }
    const payload = {
      name: form.name.trim() || buildAutoName(form, users, cronTypes, commerciaux),
      cron_type: form.cron_type,
      weekday: weekdays[0] || Number(form.weekday) || 1,
      weekdays: weekdays.length > 0 ? weekdays : [Number(form.weekday) || 1],
      hour: Number(form.hour),
      minute: Number(form.minute),
      recipient_mode: 'mixed',
      recipient_user_ids: form.recipient_user_ids,
      extra_emails: form.extra_emails,
      active: true,
      include_empty_detail: true,
      include_options: false,
    };
    if (form.cron_type === 'media_planning') {
      const mois = [...(form.filters?.mois || [])].map(Number).filter((m) => m > 0).sort((a, b) => a - b);
      const statutPhase = [...(form.filters?.statut_phase || [])]
        .map((s) => String(s || '').trim())
        .filter(Boolean);
      if (statutPhase.length === 0) {
        notify.error('Choisissez au moins un statut.');
        setWizardStep(3);
        setSaving(false);
        return;
      }
      payload.filters = {
        ...form.filters,
        mois,
        statut_phase: statutPhase,
        only_own_commercial_lines: Boolean(form.filters?.only_own_commercial_lines),
        commercial_id: form.filters?.only_own_commercial_lines
          ? ''
          : (form.filters?.commercial_id || ''),
        weekdays: payload.weekdays,
      };
    }
    if (form.cron_type === 'commerciaux_lundi') {
      const canaux = (form.filters?.canaux || [])
        .filter((id) => DEFAULT_COMMERCIAUX_LUNDI_CANAUX.includes(id));
      const statutPhase = [...(form.filters?.statut_phase || [])]
        .map((s) => String(s || '').trim())
        .filter(Boolean);
      const periodMode = form.filters?.period_mode === 'custom' ? 'custom' : 'auto';
      const mois = [...(form.filters?.mois || [])].map(Number).filter((m) => m >= 1 && m <= 12).sort((a, b) => a - b);
      if (canaux.length === 0) {
        notify.error('Choisissez au moins un support.');
        setWizardStep(2);
        setSaving(false);
        return;
      }
      if (periodMode === 'custom' && (canaux.includes('mag') || canaux.includes('set')) && mois.length === 0) {
        notify.error('Choisissez au moins un mois, ou revenez à la période automatique.');
        setWizardStep(2);
        setSaving(false);
        return;
      }
      if (statutPhase.length === 0) {
        notify.error('Choisissez au moins un statut.');
        setWizardStep(3);
        setSaving(false);
        return;
      }
      payload.filters = {
        test_mode: Boolean(form.filters?.test_mode),
        test_email: String(form.filters?.test_email || '').trim(),
        commercial_ids: (form.filters?.commercial_ids || []).map(Number).filter((id) => id > 0),
        canaux,
        statut_phase: statutPhase,
        period_mode: periodMode,
        annee: Number(form.filters?.annee) || new Date().getFullYear(),
        mois,
        window_days: normalizeCommerciauxLundiWindowDays(
          form.filters?.window_days ?? DEFAULT_COMMERCIAUX_LUNDI_WINDOW_DAYS,
        ),
        weekdays: payload.weekdays,
      };
      if (payload.filters.test_mode && !payload.filters.test_email) {
        notify.error('Essai : indiquez l’adresse de réception.');
        setSaving(false);
        return;
      }
    }
    if (form.cron_type === 'klub_etapes_lundi') {
      payload.filters = {
        test_mode: Boolean(form.filters?.test_mode),
        test_email: String(form.filters?.test_email || '').trim(),
        weekdays: payload.weekdays,
      };
      if (payload.filters.test_mode && !payload.filters.test_email) {
        notify.error('Essai : indiquez l’adresse de réception.');
        setSaving(false);
        return;
      }
    }
    if (form.cron_type === 'mails_clients_mag_set') {
      payload.filters = {
        test_mode: Boolean(form.filters?.test_mode),
        test_email: String(form.filters?.test_email || '').trim(),
        weekdays: [1, 2, 3, 4, 5, 6, 7],
      };
      if (payload.filters.test_mode && !payload.filters.test_email) {
        notify.error('Essai : indiquez l’adresse de réception.');
        setSaving(false);
        return;
      }
      const hasUsers = (form.recipient_user_ids || []).length > 0;
      const hasEmails = String(form.extra_emails || '').trim() !== '';
      if (!hasUsers && !hasEmails) {
        notify.error('Choisissez au moins une personne ou une autre adresse.');
        setSaving(false);
        return;
      }
    }
    if (form.cron_type === 'relance_vente_n1') {
      const canaux = (form.filters?.canaux || []).filter((c) => CANAUX_OPTIONS.some((opt) => opt.id === c));
      const monthDays = [...(form.filters?.month_days || DEFAULT_RELANCE_MONTH_DAYS)]
        .map(Number)
        .filter((d) => d >= 1 && d <= 31)
        .sort((a, b) => a - b);
      if (monthDays.length === 0) {
        notify.error('Choisissez au moins un jour du mois.');
        setSaving(false);
        return;
      }
      payload.filters = {
        canaux: canaux.length > 0 ? canaux : CANAUX_OPTIONS.map((c) => c.id),
        month_days: monthDays,
        weekdays: [1, 2, 3, 4, 5, 6, 7],
      };
      const hasUsers = (form.recipient_user_ids || []).length > 0;
      const hasEmails = String(form.extra_emails || '').trim() !== '';
      if (!hasUsers && !hasEmails) {
        notify.error('Choisissez au moins une personne ou une autre adresse.');
        setSaving(false);
        return;
      }
    }
    if (form.cron_type === 'pense_bete_codir') {
      const messageBody = String(form.filters?.message_body || '').trim();
      if (messageBody === '') {
        notify.error('Le message est obligatoire.');
        setSaving(false);
        return;
      }
      const hasUsers = (form.recipient_user_ids || []).length > 0;
      const hasEmails = String(form.extra_emails || '').trim() !== '';
      if (!hasUsers && !hasEmails) {
        notify.error('Choisissez au moins une personne ou une autre adresse.');
        setSaving(false);
        return;
      }
      payload.filters = {
        message_body: messageBody,
        weekdays: payload.weekdays,
      };
    }
    if (!payload.filters) {
      payload.filters = { weekdays: payload.weekdays };
    } else if (!payload.filters.weekdays) {
      payload.filters = { ...payload.filters, weekdays: payload.weekdays };
    }
    if (!isDayOfMonthCron && !isRelanceVenteN1) {
      payload.filters = {
        ...payload.filters,
        week_interval: weekInterval,
        week_anchor: weekInterval === 2 ? weekAnchor : '',
      };
    }
    try {
      if (editingId) {
        await kkpService.updateCdfDispoAlertCron(editingId, payload);
        notify.success('Alerte mise à jour');
      } else {
        await kkpService.createCdfDispoAlertCron(payload);
        notify.success('Alerte créée');
      }
      resetForm();
      await load();
      setActiveTab('crons');
    } catch (err) {
      notify.error(err?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (cron) => {
    setSaving(true);
    try {
      await kkpService.setCdfDispoAlertCronActive(cron.id, !cron.active);
      notify.success(cron.active ? 'Alerte mise en pause' : 'Alerte remise en route');
      await load();
    } catch (err) {
      notify.error(err?.message || 'Mise à jour impossible.');
    } finally {
      setSaving(false);
    }
  };

  const performRun = async (cron) => {
    setSaving(true);
    try {
      const result = await kkpService.runCdfDispoAlertCron(cron.id);
      const run = result?.run;
      if (run && Number(run.sent) === 0) {
        const detail = Array.isArray(run.errors) && run.errors[0]
          ? run.errors[0]
          : 'Aucun dossier à relancer — le mail n’a pas été envoyé.';
        notify.warning(detail);
      } else {
        notify.success('Le mail a bien été envoyé.');
      }
      await load();
    } catch (err) {
      notify.error(err?.message || 'Envoi impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async (cron) => {
    if (cron.cron_type === 'media_planning') {
      setSendModal({ show: true, cron });
      return;
    }
    setConfirmModal({ kind: 'send', cron });
  };

  const handleSendWithFilters = async (cronId, filters) => {
    setSaving(true);
    try {
      await kkpService.runCdfDispoAlertCron(cronId, filters);
      notify.success('Le mail a bien été envoyé.');
      await load();
    } catch (err) {
      notify.error(err?.message || 'Envoi impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (cron) => {
    setConfirmModal({ kind: 'delete', cron });
  };

  const performDelete = async (cron) => {
    setSaving(true);
    try {
      await kkpService.deleteCdfDispoAlertCron(cron.id);
      notify.success('Alerte supprimée');
      if (editingId === cron.id) resetForm();
      await load();
    } catch (err) {
      notify.error(err?.message || 'Suppression impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handlePermChange = (role, cronType, allowed) => {
    setPermMatrix((prev) => ({
      ...prev,
      [role]: {
        ...(prev[role] || {}),
        [cronType]: allowed,
      },
    }));
  };

  const handleSavePermissions = async () => {
    setPermSaving(true);
    try {
      const data = await kkpService.saveCronTypePermissions(permMatrix);
      setPermRoles(data.roles ?? []);
      setPermTypes(data.cron_types ?? []);
      setPermMatrix(data.matrix ?? {});
      notify.success('Droits enregistrés');
      await load();
    } catch (err) {
      notify.error(err?.message || 'Enregistrement des droits impossible.');
    } finally {
      setPermSaving(false);
    }
  };

  const confirmCron = confirmModal?.cron;
  const confirmRecipients = confirmCron
    ? formatRecipientsPhrase(formatRecipientNames(confirmCron, commerciaux))
    : '';
  const confirmIsTest = confirmCron
    && hasCronTestMode(confirmCron.cron_type)
    && Boolean(confirmCron.filters?.test_mode);

  const tabOptions = [
    { value: 'crons', label: 'Mes alertes', icon: 'mdi:email-outline', count: crons.length },
    { value: 'creer', label: editingId ? 'Modifier l’alerte' : 'Créer une alerte', icon: 'mdi:plus-circle-outline' },
    ...(isSuperAdmin
      ? [{ value: 'droits', label: 'Droits', icon: 'mdi:shield-account-outline' }]
      : []),
  ];

  if (loading) {
    return (
      <MasterLayout>
        <Breadcrumb title="Alertes emails" subtitle="Programmez qui reçoit quel mail, et quand." />
        <p className="text-secondary-light">Chargement…</p>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb title="Alertes emails" />

      <div className="d-flex flex-column gap-3">
        <div className="kkp-planning-view-toolbar justify-content-start">
          <AppPillSwitch
            options={tabOptions}
            value={activeTab}
            onChange={(value) => {
              if (value === 'creer' && !editingId) {
                beginCreate();
                return;
              }
              setActiveTab(value);
            }}
            ariaLabel="Onglets alertes mails"
          />
        </div>

        {activeTab === 'droits' && isSuperAdmin && (
          <CronTypePermissionsPanel
            roles={permRoles}
            cronTypes={permTypes}
            matrix={permMatrix}
            onChange={handlePermChange}
            onSave={handleSavePermissions}
            saving={permSaving}
          />
        )}

        {activeTab === 'creer' && (
          cronTypes.length === 0 ? (
            <AppAlert variant="warning" className="mb-0">
              Aucun type de mail n’est autorisé pour votre rôle. Demandez à un administrateur d’ouvrir l’accès.
            </AppAlert>
          ) : (
            <KkpCronsWizard
              form={form}
              setForm={setForm}
              cronTypes={cronTypes}
              users={users}
              commerciaux={commerciaux}
              meta={meta}
              editingId={editingId}
              saving={saving}
              nameTouched={nameTouched}
              setNameTouched={setNameTouched}
              wizardStep={wizardStep}
              setWizardStep={setWizardStep}
              duplicateCrons={duplicateCrons}
              currentUserEmail={currentUserEmail}
              onSubmit={handleSubmit}
              onCancel={() => {
                resetForm();
                setActiveTab('crons');
              }}
              onEditDuplicate={startEdit}
              existingCrons={crons}
              onDuplicate={startDuplicate}
            />
          )
        )}

        {activeTab === 'crons' && (
          loadError ? (
            <div className="kkp-crons-empty">
              <Icon icon="mdi:cloud-off-outline" width={36} />
              <p className="kkp-crons-empty__title">Impossible de charger les alertes</p>
              <p className="kkp-crons-empty__text">{loadError}</p>
              <button type="button" className="btn btn-primary-600" onClick={load}>
                Réessayer
              </button>
            </div>
          ) : (
            <KkpCronsList
              crons={crons}
              cronTypes={cronTypes}
              commerciaux={commerciaux}
              isSuperAdmin={isSuperAdmin}
              currentUserId={currentUserId}
              ownerScope={ownerScope}
              onOwnerScopeChange={setOwnerScope}
              search={search}
              onSearchChange={setSearch}
              saving={saving}
              onCreateType={(typeId) => beginCreate(typeId)}
              onEdit={startEdit}
              onDuplicate={startDuplicate}
              onToggleActive={handleToggleActive}
              onSend={handleRun}
              onDelete={handleDelete}
            />
          )
        )}

        <SendModal
          show={sendModal.show}
          cron={sendModal.cron}
          onClose={() => setSendModal({ show: false, cron: null })}
          meta={meta}
          commerciaux={commerciaux}
          onSend={handleSendWithFilters}
        />

        <CronConfirmModal
          show={Boolean(confirmModal)}
          title={confirmModal?.kind === 'delete' ? 'Supprimer cette alerte ?' : 'Envoyer maintenant ?'}
          confirmLabel={confirmModal?.kind === 'delete' ? 'Oui, supprimer' : 'Oui, envoyer maintenant'}
          confirmIcon={confirmModal?.kind === 'delete' ? 'mdi:delete-outline' : 'mdi:send'}
          confirmVariant={confirmModal?.kind === 'delete' ? 'danger' : 'primary'}
          busy={saving}
          onCancel={() => setConfirmModal(null)}
          onConfirm={async () => {
            const cron = confirmModal?.cron;
            const kind = confirmModal?.kind;
            setConfirmModal(null);
            if (!cron) return;
            if (kind === 'delete') {
              await performDelete(cron);
            } else {
              await performRun(cron);
            }
          }}
        >
          {confirmModal?.kind === 'delete' ? (
            <p className="mb-0">
              « <strong>{confirmCron?.name}</strong> » sera supprimée. Elle ne partira plus.
            </p>
          ) : (
            <>
              <p>
                Vous allez envoyer <strong>maintenant</strong> « {confirmCron?.name} » à{' '}
                <strong>{confirmRecipients}</strong>.
              </p>
              <p className="mb-0">
                {confirmIsTest
                  ? `C’est un essai : seul ${confirmCron?.filters?.test_email || 'l’adresse d’essai'} recevra le mail.`
                  : 'Ce n’est pas un essai : les vrais destinataires recevront le mail.'}
              </p>
            </>
          )}
        </CronConfirmModal>
      </div>
    </MasterLayout>
  );
};

export default KkpCronsPage;
